<template>
    <validation-observer ref="basicSettingsValidationObserver" v-slot="{ invalid, errors }">
        <v-card flat>
            <v-card-text class="pt-2 d-flex flex-wrap">

                <v-card flat min-height="400px" width="600px" class="mr-5">

                    <v-container>

                        <v-row dense>

                            <v-col cols="12">
                                <v-slide-x-transition>
                                    <title-card title="Panel name" v-show="dataLoaded" help="panel-settings-panel-name">
                                        <v-text-field
                                                class="pt-0"
                                                @blur="dataChanged"
                                                persistent-hint hint="Panel name"
                                                v-model="data.name"
                                                :error-messages="errorMessages['name'] ? errorMessages['name'] : []"
                                        ></v-text-field>
                                    </title-card>
                                </v-slide-x-transition>
                            </v-col>

                            <v-col cols="12">
                                <v-slide-x-transition>
                                    <title-card title="Features" v-show="dataLoaded" help="panel-settings-features">
                                        <v-container fluid class="pa-0">
                                            <v-row dense>
                                                <v-col cols=12>
                                                    <v-scale-transition>
                                                        <v-switch
                                                                class="mt-0"
                                                                @change="dataChanged"
                                                                label="Dynamic profiling"
                                                                color="primary"
                                                                v-model="data.dynamic_profiling_enabled">
                                                        </v-switch>
                                                    </v-scale-transition>

                                                    <v-scale-transition>
                                                        <v-switch
                                                                class="mt-0"
                                                                @change="dataChanged"
                                                                label="Collect survey ratings upon completion"
                                                                color="primary"
                                                                hide-details
                                                                v-model="data.survey_ratings_enabled">
                                                        </v-switch>
                                                    </v-scale-transition>
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </title-card>
                                </v-slide-x-transition>
                            </v-col>

                            <v-col cols="12">
                                <v-slide-x-transition>
                                    <title-card title="Action thresholds" v-show="dataLoaded" help="panel-settings-actions">
                                        <v-container fluid>
                                            <v-row dense>
                                                <v-col cols="12">

                                                    <v-slider
                                                            class="mt-5"
                                                            v-model="data.poor_response_rate"
                                                            hint="Send notification when response rate % falls under"
                                                            thumb-color="primary"
                                                            thumb-label="always"
                                                            persistent-hint
                                                            min="0"
                                                            max="100"
                                                            append-icon="mdi-plus"
                                                            prepend-icon="mdi-minus"
                                                            ticks
                                                            step="5"
                                                            @change="responseRateChanged"
                                                            :error-messages="errorMessages['poor_response_rate'] ? errorMessages['poor_response_rate'] : []"
                                                    ></v-slider>

                                                </v-col>

                                                <v-col cols="12">

                                                    <v-slider
                                                            class="mt-8"
                                                            v-model="data.poor_start_rate"
                                                            hint="Send notification when start rate % falls under"
                                                            thumb-color="primary"
                                                            thumb-label="always"
                                                            persistent-hint
                                                            min="0"
                                                            max="100"
                                                            append-icon="mdi-plus"
                                                            prepend-icon="mdi-minus"
                                                            ticks
                                                            step="5"
                                                            @change="dataChanged"
                                                            :error-messages="errorMessages['poor_start_rate'] ? errorMessages['poor_start_rate'] : []"
                                                    ></v-slider>

                                                </v-col>

                                                <v-col cols="12">

                                                    <v-slider
                                                        class="mt-8"
                                                        v-model="data.incidence_alarm_threshold"
                                                        hint="Send notification when incidence rate is less than % of the estimated"
                                                        thumb-color="primary"
                                                        thumb-label="always"
                                                        persistent-hint
                                                        min="0"
                                                        max="100"
                                                        append-icon="mdi-plus"
                                                        prepend-icon="mdi-minus"
                                                        ticks
                                                        step="5"
                                                        @change="incidenceAlarmThresholdChanged"
                                                        :error-messages="errorMessages['incidence_alarm_threshold'] ? errorMessages['incidence_alarm_threshold'] : []"
                                                    ></v-slider>

                                                </v-col>

                                                <v-col cols="12">
                                                    <v-slider
                                                            class="mt-8"
                                                            v-model="data.poor_survey_rating"
                                                            hint="Send notification when survey rating falls under"
                                                            thumb-color="primary"
                                                            thumb-label="always"
                                                            persistent-hint
                                                            min="0"
                                                            max="10"
                                                            append-icon="mdi-plus"
                                                            prepend-icon="mdi-minus"
                                                            ticks
                                                            @change="poorSurveyChanged"
                                                            :error-messages="errorMessages['poor_survey_rating'] ? errorMessages['poor_survey_rating'] : []"
                                                    ></v-slider>
                                                </v-col>

                                                <v-col cols=12>

                                                    <v-slider
                                                            class="mt-8"
                                                            v-model="data.speeding_threshold"
                                                            hint="Flag speeders when length of interview is less than % of expected"
                                                            thumb-color="primary"
                                                            thumb-label="always"
                                                            persistent-hint
                                                            min="0"
                                                            max="100"
                                                            step="5"
                                                            ticks
                                                            append-icon="mdi-plus"
                                                            prepend-icon="mdi-minus"
                                                            @change="speedingChanged"
                                                            :error-messages="errorMessages['speeding_threshold'] ? errorMessages['speeding_threshold'] : []"
                                                    ></v-slider>

                                                </v-col>

                                            </v-row>

                                        </v-container>

                                    </title-card>
                                </v-slide-x-transition>
                            </v-col>
                        </v-row>

                    </v-container>
                </v-card>

                <!--- ACTIVE AND ENGAGES BOXES -->
                <v-card flat min-width="600" width="600">
                    <v-container fluid>
                        <v-row dense>

                            <v-col cols=12>
                                <v-slide-x-reverse-transition>
                                    <persona-box
                                            v-show="dataLoaded"
                                            icon="mdi-face-profile"
                                            color="#2196F3"
                                            name="Active panelist"
                                            help="panel-settings-active-panelist"
                                            empty-persona-warning="Active panelist has not been defined. Click to setup."
                                            @change="activePanelistChanged"
                                            :json-query="data.active_panelist_json_query"
                                            :json-filter="data.active_panelist_json_filter">
                                    </persona-box>
                                </v-slide-x-reverse-transition>
                            </v-col>

                            <v-col cols="12">
                                <v-slide-x-reverse-transition>
                                    <persona-box
                                            v-show="dataLoaded"
                                            icon="mdi-face"
                                            color="#4CAF50"
                                            name="Engaged panelist"
                                            help="panel-settings-engaged-panelist"
                                            empty-persona-warning="Engaged panelist has not been defined. Click to setup."
                                            @change="engagedPanelistChanged"
                                            :json-query="data.engaged_panelist_json_query"
                                            :json-filter="data.engaged_panelist_json_filter">
                                    </persona-box>
                                </v-slide-x-reverse-transition>
                            </v-col>

                            <v-col cols=12>
                                <v-slide-x-reverse-transition>
                                    <title-card v-show="dataLoaded"
                                                title="Accounting currency"
                                                help="panel-settings-accounting-currency"
                                                side-icon="mdi-currency-sign">

                                        <v-alert v-if="!settings.accounting_currency && !data.accounting_currency"
                                                 type="info"
                                                 dense
                                                 class="body-2">
                                            You must setup panel accounting currency!
                                        </v-alert>

                                        <v-alert v-if="!settings.accounting_currency && data.accounting_currency"
                                                 icon="mdi-alert"
                                                 type="warning"
                                                 dense
                                                 class="body-2">
                                            Accounting currency settings cannot be changed later, you sure?
                                        </v-alert>

                                        <validation-provider
                                                name="currency unit to reward points"
                                                mode="lazy"
                                                vid="accounting_currency"
                                                rules="required|alpha|min:3|max:3"
                                                v-slot="{ errors }">

                                            <v-select v-model="data.accounting_currency"
                                                      placeholder="Please select"
                                                      label="Accounting currency"
                                                      :items="currencies"
                                                      :error-messages="errors"
                                                      :disabled="!!settings.accounting_currency"
                                            ></v-select>

                                        </validation-provider>

                                        <validation-provider
                                                name="currency unit to reward points"
                                                mode="lazy"
                                                rules="required|integer|min_value:1"
                                                v-slot="{ errors }">

                                            <v-text-field v-model="data.currency_unit_to_points"
                                                          label="Currency unit to reward points"
                                                          :error-messages="errors"
                                                          :disabled="!!settings.currency_unit_to_points"
                                            ></v-text-field>

                                        </validation-provider>

                                    </title-card>
                                </v-slide-x-reverse-transition>
                            </v-col>

                        </v-row>
                    </v-container>

                </v-card>
            </v-card-text>

            <json v-if="debug" :data="data"></json>
        </v-card>
    </validation-observer>
</template>

<script>

    import PersonaBox from './PersonaBox';
    import TitleCard from "../../../components/UI/TitleCard";
    import Json from 'vue-json-pretty';
    import {mapMutations} from 'vuex';

    export default {

        components: {
            PersonaBox,
            TitleCard,
            Json
        },

        props: {
            settings: {
                type: Object,
                required: true
            },
            errorMessages: {
                type: Object,
                required: false,
            },
            currencies: {
                type: Array,
                required: true,
            }
        },

        data() {
            return {
                debug: false,
                data: {
                    name: null,
                    poor_response_rate: null,
                    poor_completion_rate: null,
                    poor_survey_rating: null,
                    dynamic_profiling_enabled: null,
                    incidence_alarm_threshold: null,
                    survey_ratings_enabled: null,
                    speeding_threshold: null,
                    active_panelist_json_query: null,
                    active_panelist_json_filter: null,
                    engaged_panelist_json_filter: null,
                    engaged_panelist_json_query: null,
                    accounting_currency: null,
                    currency_unit_to_points: null,
                },
            }
        },

        mounted() {
            this.copySettings(this.settings);
        },

        watch: {
            settings(settings) {
                this.copySettings(settings);
            },
        },

        computed: {

            dataLoaded() {
                if (!!this.settings.id) {
                    this.$emit('change', this.data);
                    return true;
                }
                return false;
            },
        },

        methods: {

            ...mapMutations('Health', [
                'clearHealthCache'
            ]),

            ...mapMutations('Financials', [
                'clearFinancialCache'
            ]),


            copySettings(settings) {
                this.data = JSON.parse(JSON.stringify(this.settings));
            },

            responseRateChanged(value) {
                if (value === 0) {
                    Bus.$emit('flash-message', {type: 'info', message: 'Response rate notifications are now disabled!'})
                }
                this.dataChanged();
            },

            poorSurveyChanged(value) {
                if (value === 0) {
                    Bus.$emit('flash-message', {type: 'info', message: 'Survey rating notifications are now disabled!'})
                }
                this.dataChanged();
            },

            speedingChanged(value) {
                if (value === 0) {
                    Bus.$emit('flash-message', {type: 'info', message: 'Speeder flagging is now disabled!'});
                }
                this.dataChanged();
            },

            incidenceAlarmThresholdChanged(value) {
                if (value === 0) {
                    Bus.$emit('flash-message', {type: 'info', message: 'Incidence alarm disabled!'});
                }
            },

            dataChanged() {

                // Do not validate anything while loading form data
                // this caused variety emit that need to be ignored
                if (this.dataLoaded) {
                    this.$refs.basicSettingsValidationObserver.validate()
                        .then(isValid => {
                            if (isValid) {
                                this.$emit('change', this.data);
                            }
                        });
                }
            },

            activePanelistChanged(segment) {
                this.clearCaches();
                this.data.active_panelist_json_query = segment.json_query;
                this.data.active_panelist_json_filter = segment.json_filter;
                this.dataChanged();
            },

            engagedPanelistChanged(segment) {
                this.clearCaches();
                this.data.engaged_panelist_json_query = segment.json_query;
                this.data.engaged_panelist_json_filter = segment.json_filter;
                this.dataChanged();
            },

            clearCaches() {
                if (this.$store.state['Health']) {
                    this.clearHealthCache();
                }

                if (this.$store.state['Financials']) {
                    this.clearFinancialCache();
                }
            }
        }
    }
</script>
